import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Lock, Unlock, Loader2, DollarSign, AlertTriangle, Info } from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency } from '../../lib/utils';
import { useAuth } from '../../contexts/AuthContext';
import {
  getCurrentSession,
  openCashSession,
  closeCashSession,
} from '../../lib/api';

/**
 * Compact status bar for the current cash session.
 * - For cajero: auto-uses their assigned branch.
 * - For admin: pass `branchId` prop (required to operate).
 *
 * `onSessionChange(session|null)` callback notifies the parent so the POS
 * can block / unblock actions based on session state.
 */
export default function CashSessionBar({ branchId = null, onSessionChange = () => {} }) {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [session, setSession] = useState(null);
  const [openingsToday, setOpeningsToday] = useState(0);
  const [maxOpenings, setMaxOpenings] = useState(2);
  const [loading, setLoading] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const [closeModal, setCloseModal] = useState(false);
  const [busy, setBusy] = useState(false);
  const [openForm, setOpenForm] = useState({ opening_amount: '', notes: '' });
  const [closeForm, setCloseForm] = useState({ closing_amount: '', notes: '' });

  const fetchCurrent = useCallback(async () => {
    try {
      const { data } = await getCurrentSession(branchId);
      setSession(data.session || null);
      setOpeningsToday(data.openings_today ?? 0);
      setMaxOpenings(data.max_openings ?? 2);
      onSessionChange(data.session || null);
    } catch (e) {
      setSession(null);
      onSessionChange(null);
    }
    setLoading(false);
  }, [branchId, onSessionChange]);

  useEffect(() => {
    fetchCurrent();
  }, [fetchCurrent]);

  const handleOpen = async (e) => {
    e.preventDefault();
    const amount = parseFloat(openForm.opening_amount);
    if (Number.isNaN(amount) || amount < 0) {
      toast.error('Monto de apertura inválido');
      return;
    }
    setBusy(true);
    try {
      await openCashSession({
        opening_amount: amount,
        branch_id: branchId || undefined,
        notes: openForm.notes || null,
      });
      toast.success('Caja abierta');
      setOpenModal(false);
      setOpenForm({ opening_amount: '', notes: '' });
      await fetchCurrent();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al abrir caja');
    }
    setBusy(false);
  };

  const handleClose = async (e) => {
    e.preventDefault();
    const amount = parseFloat(closeForm.closing_amount);
    if (Number.isNaN(amount) || amount < 0) {
      toast.error('Monto de cierre inválido');
      return;
    }
    setBusy(true);
    const closedSessionId = session?.id;
    try {
      const { data } = await closeCashSession(closedSessionId, {
        closing_amount: amount,
        notes: closeForm.notes || null,
      });
      const diff = data.difference || 0;
      if (Math.abs(diff) < 0.01) {
        toast.success('Caja cerrada · cuadre exacto');
      } else if (diff > 0) {
        toast.success(`Caja cerrada · sobrante ${formatCurrency(diff)}`);
      } else {
        toast.error(`Caja cerrada · faltante ${formatCurrency(Math.abs(diff))}`);
      }
      setCloseModal(false);
      setCloseForm({ closing_amount: '', notes: '' });
      await fetchCurrent();
      // Redirect to cash report for the just-closed session
      if (closedSessionId) navigate(`/admin/caja/${closedSessionId}/reporte`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al cerrar caja');
    }
    setBusy(false);
  };

  if (loading) {
    return (
      <div className="px-3 py-2 rounded-lg bg-slate-100 text-slate-500 text-sm flex items-center gap-2">
        <Loader2 className="w-4 h-4 animate-spin" />
        Caja...
      </div>
    );
  }

  // No session → show "Abrir caja" button
  if (!session) {
    const cashierBlocked = !isAdmin && openingsToday >= maxOpenings;
    if (cashierBlocked) {
      return (
        <div className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-amber-200 bg-amber-50 text-amber-700 text-sm" data-testid="cash-closed-cajero-msg">
          <Info className="w-4 h-4 shrink-0" />
          <span>Ya completaste 2 aperturas hoy · solicita al administrador que abra una nueva</span>
        </div>
      );
    }
    return (
      <>
        <Button
          variant="outline"
          onClick={() => setOpenModal(true)}
          className="gap-2 border-red-300 bg-red-50 hover:bg-red-100 text-red-700"
          data-testid="open-cash-btn"
        >
          <Lock className="w-4 h-4" />
          Caja cerrada · Abrir
        </Button>

        <Dialog open={openModal} onOpenChange={setOpenModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Unlock className="w-5 h-5 text-emerald-600" />
                Abrir caja
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleOpen} className="space-y-4">
              <div>
                <Label htmlFor="opening-amount">Efectivo de apertura *</Label>
                <div className="relative mt-1">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="opening-amount"
                    type="number"
                    min="0"
                    step="0.01"
                    className="pl-9 text-lg font-mono font-semibold"
                    placeholder="0.00"
                    value={openForm.opening_amount}
                    onChange={(e) => setOpenForm({ ...openForm, opening_amount: e.target.value })}
                    autoFocus
                    data-testid="opening-amount-input"
                  />
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  Cuenta el efectivo físico con el que inicias el turno.
                </p>
              </div>
              <div>
                <Label htmlFor="opening-notes">Notas (opcional)</Label>
                <Textarea
                  id="opening-notes"
                  placeholder="Ej: incluye fondo fijo de 200..."
                  value={openForm.notes}
                  onChange={(e) => setOpenForm({ ...openForm, notes: e.target.value })}
                  className="mt-1"
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setOpenModal(false)} disabled={busy}>Cancelar</Button>
                <Button type="submit" disabled={busy} data-testid="confirm-open-cash-btn">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Abrir caja'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  const expected = session.current_expected ?? session.opening_amount ?? 0;

  return (
    <>
      <Button
        variant="outline"
        onClick={() => setCloseModal(true)}
        className="gap-2 border-emerald-300 bg-emerald-50 hover:bg-emerald-100 text-emerald-700"
        data-testid="close-cash-btn"
      >
        <Unlock className="w-4 h-4" />
        <span>Caja abierta · <span className="font-mono font-bold">{formatCurrency(expected)}</span></span>
      </Button>

      <Dialog open={closeModal} onOpenChange={setCloseModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="w-5 h-5 text-red-600" />
              Cerrar caja
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Summary */}
            <div className="grid grid-cols-2 gap-3 p-3 bg-slate-50 rounded-xl text-sm">
              <div>
                <div className="text-xs text-slate-500">Apertura</div>
                <div className="font-mono font-semibold">{formatCurrency(session.opening_amount || 0)}</div>
              </div>
              <div>
                <div className="text-xs text-slate-500">Ingresos</div>
                <div className="font-mono font-semibold text-emerald-600">+{formatCurrency(session.ingresos_total || 0)}</div>
              </div>
              <div>
                <div className="text-xs text-slate-500">Egresos</div>
                <div className="font-mono font-semibold text-red-600">-{formatCurrency(session.egresos_total || 0)}</div>
              </div>
              <div>
                <div className="text-xs text-slate-500">Esperado</div>
                <div className="font-mono font-bold text-blue-700">{formatCurrency(expected)}</div>
              </div>
            </div>

            <form onSubmit={handleClose} className="space-y-4">
              <div>
                <Label htmlFor="closing-amount">Efectivo contado *</Label>
                <div className="relative mt-1">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="closing-amount"
                    type="number"
                    min="0"
                    step="0.01"
                    className="pl-9 text-lg font-mono font-semibold"
                    placeholder="0.00"
                    value={closeForm.closing_amount}
                    onChange={(e) => setCloseForm({ ...closeForm, closing_amount: e.target.value })}
                    autoFocus
                    data-testid="closing-amount-input"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="closing-notes">Notas (opcional)</Label>
                <Textarea
                  id="closing-notes"
                  placeholder="Ej: entregué $500 al admin..."
                  value={closeForm.notes}
                  onChange={(e) => setCloseForm({ ...closeForm, notes: e.target.value })}
                  className="mt-1"
                />
              </div>

              <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                El sistema comparará el efectivo contado contra el esperado. Un faltante o sobrante quedará registrado en el historial.
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setCloseModal(false)} disabled={busy}>Cancelar</Button>
                <Button
                  type="submit"
                  className="bg-red-600 hover:bg-red-700 text-white"
                  disabled={busy}
                  data-testid="confirm-close-cash-btn"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Cerrar caja'}
                </Button>
              </DialogFooter>
            </form>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
